import { useParams } from 'react-router-dom';
import {
  useGetProjectByIdQuery,
  useGetZendeskEmailStatusFieldsQuery,
  useLazyGetZendeskConsentLinkQuery,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect, useState } from 'react';
import { EditZendeskEmailAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskEmailIntegrationSettings/EditZendeskEmailAutoSetupSettings/EditZendeskEmailAutoSetupSettings';
import { EditZendeskEmailAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskEmailIntegrationSettings/EditZendeskEmailAdvancedSettings/EditZendeskEmailAdvancedSettings';
import { EditZendeskEmailApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskEmailIntegrationSettings/EditZendeskEmailApiDetailsSettings/EditZendeskEmailApiDetailsSettings';
import { useAppSelector, useAppDispatch } from 'src/store';
import { ProductsTypeBack } from 'src/constants/products';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useIntegrationAuthorizationPolling } from 'src/components/Integrations/useIntegrationAuthorizationPolling';

const InitialStatusFields = [
  { id: 'dont-change-ticket-status', agent_label: 'don’t change ticket status' },
];

interface ZendeskEmailSettingsFormValues extends AutocloseFormValues {
  subdomain: string;
  email: string;
  include_subject_first_response: boolean;
  reply_to_note: boolean;
  ticket_status_after_ai_response: string;
  ticket_status_after_escalation: string;
  api_key: string;
  include_custom_fields: boolean;
  custom_fields: { key?: string; id: string; name: string }[] | null;
}

export const EditZendeskEmailIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [statusFields, setStatusFields] = useState([]);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { data: statusFieldsResponse, isError: isErrorStatusFields } =
    useGetZendeskEmailStatusFieldsQuery(
      {
        projectId: id,
        integrationId: selectedIntegration?.id,
        type: ProductsTypeBack[currentProject?.project_type],
      },
      { skip: !selectedIntegration?.id || !currentProject },
    );
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);
  const dispatch = useAppDispatch();
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(
    Boolean(integration?.integration_data?.email),
  );
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isAuthorized = integration?.integration_data?.auth_status === 'ok';
  const isZendeskOAuthUsed = integration?.integration_data?.auth_type === 'oauth';
  const isManualConnectActive = isManualConnectOpen && !isZendeskOAuthUsed;
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useIntegrationAuthorizationPolling(id);

  useLoaderWhile(isLoadingUpdateProject);

  useEffect(() => {
    if (statusFieldsResponse) {
      const fields = [...InitialStatusFields, ...statusFieldsResponse].map((field) => {
        return {
          ...field,
          agent_label: field.agent_label.toLowerCase(),
        };
      });

      setStatusFields(fields);
    }
  }, [statusFieldsResponse]);

  const methods = useForm<ZendeskEmailSettingsFormValues>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain,
      email: integration?.integration_data?.email || '',
      include_subject_first_response: integration?.integration_data?.include_subject_first_response,
      reply_to_note: integration?.integration_data?.reply_to_note,
      ticket_status_after_ai_response:
        integration?.integration_data?.ticket_status_after_ai_response,
      ticket_status_after_escalation: integration?.integration_data?.ticket_status_after_escalation,
      api_key: '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      include_custom_fields: integration?.integration_data?.include_custom_fields || false,
      custom_fields: integration?.integration_data?.custom_fields || null,
    },
    resolver: formResolver(
      object({
        subdomain: stringScheme({ required: false }).label('Zendesk subdomain'),
        api_key: stringScheme({ required: false }).label('API token'),
        email: stringScheme({ required: false }).label('Admin email address'),
      }),
    ),
  });

  const openZendeskConsentPage = async () => {
    const { data } = await getZendeskConsentLinkQuery({
      project_id: id,
      integration_id: integration.id,
      type: 'zendesk-email',
    });
    if (!data?.authorize_url) return false;

    window.open(data.authorize_url);
    return true;
  };

  const onConnectWithZendesk = async () => {
    if (integration?.id) {
      if (await openZendeskConsentPage()) {
        pollZendeskAuthorization(integration.id, (authorizedIntegration) =>
          dispatch(setSelectedIntegration({ ...integration, ...authorizedIntegration })),
        );
      }
    }
  };

  // The integration is already authorized, so there is no status change to poll for.
  const onReconnectWithZendesk = async () => {
    if (integration?.id) {
      await openZendeskConsentPage();
    }
  };

  // Runs the same validation as submit; the connect handlers only fire for a valid form.
  const connectWithZendesk = methods.handleSubmit(onConnectWithZendesk);
  const reconnectWithZendesk = methods.handleSubmit(onReconnectWithZendesk);

  const submit = async (data: ZendeskEmailSettingsFormValues) => {
    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (!data.api_key) {
      delete data.api_key;
    }

    if (data.include_custom_fields) {
      data.custom_fields = data.custom_fields.map((field) => {
        return { id: field.id, name: `${field.name}` };
      });
    } else {
      data.custom_fields = null;
    }

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
        subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
        project_name: currentProject.name,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="ticket-status-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Ticket status settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon
                    open={accordionValue.includes('ticket-status-settings')}
                  />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskEmailAutoSetupSettings
                statusFields={statusFields}
                isError={isErrorStatusFields}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskEmailAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskEmailApiDetailsSettings
                isAuthorized={isAuthorized}
                isZendeskOAuthUsed={isZendeskOAuthUsed}
                isManualConnectActive={isManualConnectActive}
                isCheckingAuthStatus={isCheckingAuthStatus}
                onManualConnectOpenChange={setIsManualConnectOpen}
                onConnectWithZendesk={connectWithZendesk}
                onReconnectWithZendesk={reconnectWithZendesk}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
